import { throttle, debounce } from 'throttle-debounce';

import { BREAKPOINTS } from '~~/common/js/constants/screen';
import {
  getBreakpoint,
} from '~~/common/js/client/screen';
import timeouts from '~~/common/js/constants/timeouts';

export const DEFAULT_INTERVAL = timeouts.SENSITIVE;

const recognisedBreakpoints = {
  TABLET: 'isTabletUp',
  DESKTOP: 'isDesktopUp',
};

/**
 * Takes a breakpoint object as defined in common/js/constants
 * and returns a MediaQueryList which watches that breakpoint in the viewport
 * https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList
 *
 * @param {Object} breakpoint the breakpoint data
 */
const createMediaQueryList = ({ minWidth }) => {
  const list = window.matchMedia?.(`(min-width: ${minWidth}px)`);
  if (!list) {
    return null;
  }
  // fix for Safari only supporting non-standard `add[remove]Listener` methods
  list.addEventListener = list.addEventListener ??
    ((event, handler) => list.addListener(handler));
  list.removeEventListener = list.removeEventListener ??
    ((event, handler) => list.removeListener(handler));
  return list;
};

// we only have one set of MediaQueryLists per module load
let mediaQueryLists = null;
// Initialises shared MediaQueryLists (called the first time the mixin is mounted client-side)
const createMediaQueryLists = () => {
  mediaQueryLists = Object.entries(BREAKPOINTS)
    .filter(([key]) => Object.keys(recognisedBreakpoints).includes(key))
    .map(([key, breakpoint]) => {
      const mediaQueryList = createMediaQueryList(breakpoint);
      if (!mediaQueryList) {
        return null;
      }

      return {
        key,
        mediaQueryList,
      };
    })
    // get rid of breakpoints for which we couldn't create a MediaQueryList
    .filter((list) => !!list);
};

/**
 * Creates a Vue mixin which adds reactive properties for each export of the common screen.js module
 * to the VM. These properties are updated when the viewport is resized.
 *
 * By default, the mixin uses the matchMedia API to update the reactive properties. It also lets
 * the Vue component easily add a controlled window resize listner for more granular control.
 *
 * @param {string} resizeListener the name of the component method to call as resize callback
 * @param {string} mode control mode for the resize listener - 'debounced' (default) or 'throttled'
 * @param {string} interval control interval in milliseconds
 */
export default (resizeListener = null, mode = 'debounced', interval = DEFAULT_INTERVAL) => ({
  data: () => ({
    breakpoint: null,
    isTabletUp: null,
    isDesktopUp: null,
  }),
  mounted() {
    if (resizeListener) {
      this.createResizeListener();
    }

    if (!mediaQueryLists) {
      createMediaQueryLists();
    }
    this.addMediaQueryListeners();
  },
  beforeDestroy() {
    if (resizeListener) {
      window.removeEventListener('resize', this.screenResizeListener);
    }

    if (this.mediaQueryListenerMap) {
      for (const [mediaQueryList, listener] of this.mediaQueryListenerMap) {
        mediaQueryList.removeEventListener('change', listener);
      }
    }
  },
  methods: {
    createResizeListener() {
      this.screenResizeListener = () => {
        if (this[resizeListener]) {
          this[resizeListener]();
        }
      };
      // TODO: see if we could memoize the listener per mode+interval
      const controller = mode === 'throttled' ? throttle : debounce;
      window.addEventListener('resize', controller(interval, this.screenResizeListener));
      this.screenResizeListener();
    },
    addMediaQueryListeners() {
      this.mediaQueryListenerMap = new Map();

      mediaQueryLists.forEach(({ key, mediaQueryList }) => {
        const listener = ({ matches }) => {
          this.breakpoint = getBreakpoint();
          this[recognisedBreakpoints[key]] = matches;
        };

        mediaQueryList.addEventListener('change', listener);
        // run the listener once with the current value
        listener({ matches: mediaQueryList.matches });
        // store the listener so it can be removed later
        this.mediaQueryListenerMap.set(mediaQueryList, listener);
      });
    },
  },
});
